import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  TrendingUp,
  TrendingDown,
  Wallet,
  ShieldCheck,
  ShieldAlert,
  Milestone,
  CheckCircle2,
  ArrowUpRight,
  PiggyBank,
  Phone,
  CalendarClock,
  AlertTriangle,
  Check,
  RotateCcw,
  Loader2,
  Banknote,
} from "lucide-react";

import DashboardLayout from "../components/layout/DashboardLayout";
import DateField from "../components/ui/DateField";

import { useAuth } from "../context/AuthContext";

import { api, fmtINR, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useOnboardingStore } from "../store/useOnboardingStore";

import OnboardingOverlay from "../onboarding/OnboardingOverlay";

import { dashboardTour } from "../onboarding/tours/dashboardTour";

import PlanningSection from "../components/planning/PlanningSection";

import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";

const STRESS_COLOR = {
  Minimal: "#10b981",
  Low: "#84cc16",
  Moderate: "#f59e0b",
  High: "#f97316",
  Critical: "#ef4444",
};
const STRESS_TEXT = {
  Minimal: "text-emerald-600",
  Low: "text-lime-600",
  Moderate: "text-amber-600",
  High: "text-orange-600",
  Critical: "text-rose-600",
};

const COLORS = [
  "#10b981",
  "#2563eb",
  "#f59e0b",
  "#a855f7",
  "#ef4444",
  "#14b8a6",
  "#fb923c",
  "#64748b",
];

const CATEGORY_LABELS = {
  mutual_funds: "Mutual Funds",
  stocks: "Stocks",
  bank: "Bank Accounts",
  fd: "Fixed Deposits",
  rd: "Recurring Deposits",
  mis: "Monthly Income Scheme",
  bonds: "Bonds",
  gold: "Gold",
  silver: "Silver",
  crypto: "Crypto",
  real_estate: "Real Estate",
  vehicle: "Vehicles",
  business: "Business",
  insurance: "Insurance",
  epf: "EPF / PPF",
  nps: "NPS",
  cash: "Cash",
  collectibles: "Collectibles",
  digital: "Digital Assets",
  other: "Other",
};

const HEALTH_ACCENT = (s) =>
  s >= 80
    ? "text-emerald-600"
    : s >= 65
      ? "text-blue-600"
      : s >= 45
        ? "text-amber-600"
        : "text-rose-600";

const HEALTH_STROKE = (s) =>
  s >= 80 ? "#10b981" : s >= 65 ? "#2563eb" : s >= 45 ? "#f59e0b" : "#ef4444";

export default function Dashboard() {
  const { user } = useAuth();
  // Select narrowly so transient tour state (open/step) doesn't re-trigger this.
  const openTour = useOnboardingStore((s) => s.openTour);
  const dashboardDone = useOnboardingStore((s) =>
    s.completedTours.includes("dashboard"),
  );

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/summary");
        setSummary(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) return; // wait for auth to resolve
    // Already done — either on the server (cross-device) or locally (this skip).
    if (user.has_completed_onboarding || dashboardDone) return;
    const timer = setTimeout(() => openTour("dashboard"), 1000);
    return () => clearTimeout(timer);
  }, [user, loading, openTour, dashboardDone]);

  return (
    <DashboardLayout>
      <div data-tour="dashboard-welcome">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500">
              Welcome back
            </p>
            <h1
              data-testid="dashboard-title"
              className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900 dark:text-white"
            >
              Hi {user?.name?.split(" ")[0] || "there"}
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Here&apos;s a live snapshot of your financial future.
            </p>
          </div>

          <Link
            to="/dashboard/assets"
            data-testid="add-asset-cta"
            data-tour="add-asset-button"
            className="btn-primary !text-xs !py-2 !px-4"
          >
            Add new asset →
          </Link>
        </div>
      </div>

      {loading ? <Skeleton /> : <DashboardBody summary={summary} />}
      <OnboardingOverlay steps={dashboardTour} />
      <ProfileCompletionGate />
    </DashboardLayout>
  );
}

function DashboardBody({ summary }) {
  if (!summary) return null;

  const {
    total_assets,
    total_liabilities,
    net_worth,
    asset_count,
    liability_count,
    monthly_emi,
    debt_to_asset_ratio,
    projected_net_worth,
    debt_free_year,
    allocation,
    allocation_insights,
    financial_health_score,
    wealth_projection,
    stress_timeline,
    financial_milestones,
  } = summary;

  const allocationData = (allocation || [])
    .map((a) => ({
      name: CATEGORY_LABELS[a.name] || a.name,
      value: Number(a.value || 0),
    }))
    .filter((a) => a.value > 0)
    .sort((a, b) => b.value - a.value);

  return (
    <>
      {/* ============ HERO: Net worth + Health ============ */}
      <section data-tour="networth-card" className="grid lg:grid-cols-3 gap-6">
        <NetWorthHero
          netWorth={net_worth}
          totalAssets={total_assets}
          totalLiabilities={total_liabilities}
          assetCount={asset_count}
          liabilityCount={liability_count}
          debtRatio={debt_to_asset_ratio}
          projected={projected_net_worth}
          projection={wealth_projection}
        />
        <HealthGauge
          score={financial_health_score}
          debtRatio={debt_to_asset_ratio}
          insights={allocation_insights}
        />
      </section>

      {/* ============ Retirement & FI (kept) ============ */}
      <PlanningSection />

      {/* ============ Projection + Allocation ============ */}
      <section className="grid lg:grid-cols-3 gap-6 mt-6">
        <ProjectionCard projection={wealth_projection} />
        <AllocationDonut
          data={allocationData}
          insights={allocation_insights}
          classesTracked={allocationData.length}
        />
      </section>

      {/* ============ Monthly income (MIS) ============ */}
      <section className="mt-6">
        <MonthlyIncomeCard />
      </section>

      {/* ============ EMI deadlines ============ */}
      <section className="mt-6">
        <EmiTracker />
      </section>

      {/* ============ Debt & Stress + Milestones ============ */}
      <section className="grid lg:grid-cols-2 gap-6 mt-6">
        <DebtCard
          totalLiabilities={total_liabilities}
          monthlyEMI={monthly_emi}
          debtFreeYear={debt_free_year}
          stressTimeline={stress_timeline}
        />
        <MilestonesCard milestones={financial_milestones} />
      </section>
    </>
  );
}

// =====================================================================
// EMI DEADLINES — upcoming EMIs with monthly acknowledgment
// =====================================================================
const EMI_STATUS = {
  overdue: {
    label: "Overdue",
    chip: "bg-rose-500/15 text-rose-600 dark:text-rose-400",
    dot: "bg-rose-500",
    ring: "border-rose-200 dark:border-rose-500/25",
  },
  due_soon: {
    label: "Due soon",
    chip: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
    dot: "bg-amber-500",
    ring: "border-amber-200 dark:border-amber-500/25",
  },
  upcoming: {
    label: "Upcoming",
    chip: "bg-sky-500/15 text-sky-600 dark:text-sky-400",
    dot: "bg-sky-400",
    ring: "border-slate-200 dark:border-slate-800",
  },
  cleared: {
    label: "Paid this month",
    chip: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
    dot: "bg-emerald-500",
    ring: "border-emerald-200 dark:border-emerald-500/25",
  },
};

const prettyCategory = (c) =>
  String(c || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());

const dueLabel = (days) => {
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return "Due today";
  if (days === 1) return "Due tomorrow";
  return `in ${days} days`;
};

function EmiTracker() {
  const [emis, setEmis] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/emi/upcoming");
      setEmis(data.emis || []);
      setSummary(data.summary || null);
    } catch {
      // Silent — the tracker is a secondary dashboard widget.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Optimistically flip one row so the UI updates instantly, then reconcile
  // with the server (which also handles the cycle roll-over).
  const patchItem = (liabilityId, patch) =>
    setEmis((prev) =>
      prev.map((it) =>
        it.liability_id === liabilityId ? { ...it, ...patch } : it,
      ),
    );

  const acknowledge = async (item) => {
    setBusyId(item.liability_id);
    patchItem(item.liability_id, { acknowledged: true, status: "cleared" });
    try {
      await api.post(`/emi/${item.liability_id}/acknowledge`, {
        period: item.ack_period || item.period,
      });
      toast.success(`${item.name} marked paid for this cycle`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const undo = async (item) => {
    setBusyId(item.liability_id);
    // Instant feedback: unmark and recompute this row's status locally.
    const days = item.days_until;
    const optimisticStatus =
      days < 0 ? "overdue" : days <= 10 ? "due_soon" : "upcoming";
    patchItem(item.liability_id, {
      acknowledged: false,
      status: optimisticStatus,
    });
    try {
      await api.delete(
        `/emi/${item.liability_id}/acknowledge?period=${encodeURIComponent(
          item.ack_period || item.period,
        )}`,
      );
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
      await load();
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return null;
  if (!emis.length) return null;

  return (
    <div className="card-soft p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <CardHeader
          icon={CalendarClock}
          eyebrow="Cash flow"
          title="EMI deadlines"
        />
        <div className="flex items-center gap-4 text-right">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-400">
              Monthly EMI
            </div>
            <div className="font-display text-lg font-bold text-slate-900 dark:text-white">
              {fmtINR(summary?.total_monthly_emi || 0)}
            </div>
          </div>
          {summary?.action_needed > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 px-3 py-1.5 text-xs font-semibold text-amber-600 dark:text-amber-400">
              <AlertTriangle className="h-3.5 w-3.5" />
              {summary.action_needed} to pay
            </span>
          )}
        </div>
      </div>

      <div className="mt-5 space-y-2.5">
        {emis.map((item) => {
          const st = EMI_STATUS[item.status] || EMI_STATUS.upcoming;
          const due = new Date(item.due_date + "T00:00:00");
          const dueStr = due.toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
          });
          const busy = busyId === item.liability_id;
          return (
            <div
              key={item.liability_id}
              className={`flex items-center justify-between gap-3 rounded-2xl border ${st.ring} bg-white dark:bg-slate-900/40 px-4 py-3`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span
                  className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${st.dot}`}
                />
                <div className="min-w-0">
                  <div className="font-semibold text-sm text-slate-900 dark:text-white truncate">
                    {item.name}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {prettyCategory(item.category)}
                    {item.lender ? ` · ${item.lender}` : ""} ·{" "}
                    {item.status === "cleared" ? "Next due" : "Due"} {dueStr}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 flex-shrink-0">
                <div className="text-right hidden sm:block">
                  <div className="font-semibold text-sm text-slate-900 dark:text-white">
                    {fmtINR(item.emi)}
                  </div>
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${st.chip}`}
                  >
                    {item.status === "cleared"
                      ? st.label
                      : dueLabel(item.days_until)}
                  </span>
                </div>

                {item.acknowledged ? (
                  // Paid = a clear status chip + an explicit Undo (in case the
                  // "Mark paid" button was tapped by mistake).
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500/10 px-2.5 py-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Paid
                    </span>
                    <button
                      onClick={() => undo(item)}
                      disabled={busy}
                      title="Undo — mark this EMI as unpaid"
                      className="inline-flex items-center gap-1 rounded-lg border border-slate-200 dark:border-slate-700 px-2.5 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition disabled:opacity-50"
                    >
                      {busy ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <RotateCcw className="h-3.5 w-3.5" />
                      )}
                      Undo
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => acknowledge(item)}
                    disabled={busy}
                    className="inline-flex items-center gap-1.5 rounded-lg btn-primary px-3 py-1.5 text-xs font-semibold text-white dark:text-slate-900 hover:opacity-90 transition disabled:opacity-50"
                  >
                    {busy ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Check className="h-3.5 w-3.5" />
                    )}
                    Mark paid
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className="mt-4 text-[11px] text-slate-400">
        Mark an EMI paid to clear it for the month. It reappears — and you'll
        get a reminder — when the next due date is within 10 days.
      </p>
    </div>
  );
}

// =====================================================================
// MONTHLY INCOME — passive income from Monthly Income Scheme (MIS) assets
// =====================================================================
function MonthlyIncomeCard() {
  const [schemes, setSchemes] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/assets");
        const mis = (data.assets || [])
          .filter((a) => a.category === "mis" && !a.synthetic)
          .map((a) => ({
            name: a.name,
            monthly:
              (Number(a.value || 0) * Number(a.expected_return || 0)) / 1200,
          }))
          .filter((s) => s.monthly > 0)
          .sort((a, b) => b.monthly - a.monthly);
        setSchemes(mis);
      } catch {
        /* silent — secondary widget */
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  if (!loaded || schemes.length === 0) return null;

  const totalMonthly = schemes.reduce((s, x) => s + x.monthly, 0);
  const max = Math.max(...schemes.map((s) => s.monthly), 1);
  const COLORS_MI = ["#10b981", "#2563eb", "#f59e0b", "#a855f7", "#14b8a6", "#fb923c"];

  return (
    <div className="card-soft p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <CardHeader
          icon={Banknote}
          eyebrow="Passive income"
          title="Monthly income"
        />
        <div className="text-right">
          <div className="font-display text-3xl font-bold text-emerald-600">
            {fmtINR(totalMonthly)}
            <span className="text-sm font-medium text-slate-400">/mo</span>
          </div>
          <div className="text-xs text-slate-500">
            {fmtINR(totalMonthly * 12)} per year · {schemes.length} scheme
            {schemes.length === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      {/* Income-by-scheme bar graph */}
      <div className="mt-5 space-y-3">
        {schemes.map((s, i) => (
          <div key={`${s.name}-${i}`}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="font-medium text-slate-700 dark:text-slate-200 truncate pr-2">
                {s.name}
              </span>
              <span className="num-mono font-semibold text-slate-900 dark:text-white whitespace-nowrap">
                {fmtINR(s.monthly)}
                <span className="text-slate-400 font-normal">/mo</span>
              </span>
            </div>
            <div className="h-2.5 w-full rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${Math.max(6, (s.monthly / max) * 100)}%`,
                  backgroundColor: COLORS_MI[i % COLORS_MI.length],
                }}
              />
            </div>
          </div>
        ))}
      </div>

      <p className="mt-4 text-[11px] text-slate-400">
        Estimated from each scheme's value × interest rate ÷ 12. Add a{" "}
        <span className="font-medium">Monthly Income Scheme</span> asset to grow
        this.
      </p>
    </div>
  );
}

// =====================================================================
// HERO — Net worth headline + assets/liabilities + leverage + trend
// =====================================================================
function NetWorthHero({
  netWorth,
  totalAssets,
  totalLiabilities,
  assetCount,
  liabilityCount,
  debtRatio,
  projected,
  projection,
}) {
  const growth = projected - netWorth;
  const growthPct = netWorth > 0 ? (growth / netWorth) * 100 : null;

  return (
    <div className="lg:col-span-2 card-soft p-6 flex flex-col">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">
            Total Net Worth
          </div>
          <div
            data-testid="stat-net-worth"
            className="num-mono text-4xl font-bold text-slate-900 dark:text-white mt-1"
          >
            {fmtINR(netWorth)}
          </div>
        </div>
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600">
          <Wallet className="h-5 w-5" />
        </div>
      </div>

      {/* Assets / Liabilities split */}
      <div className="grid grid-cols-2 gap-3 mt-5">
        <div
          data-testid="stat-total-assets"
          className="rounded-xl border border-slate-200 dark:border-slate-700 p-3"
        >
          <div className="flex items-center gap-1.5 text-emerald-600 text-xs font-semibold">
            <TrendingUp className="h-3.5 w-3.5" /> Assets
          </div>
          <div className="num-mono text-lg font-bold text-slate-900 dark:text-white mt-1">
            {fmtINR(totalAssets)}
          </div>
          <div className="text-[11px] text-slate-500">
            {assetCount} holdings
          </div>
        </div>
        <div
          data-testid="stat-total-liabilities"
          className="rounded-xl border border-slate-200 dark:border-slate-700 p-3"
        >
          <div className="flex items-center gap-1.5 text-rose-600 text-xs font-semibold">
            <TrendingDown className="h-3.5 w-3.5" /> Liabilities
          </div>
          <div className="num-mono text-lg font-bold text-slate-900 dark:text-white mt-1">
            {fmtINR(totalLiabilities)}
          </div>
          <div className="text-[11px] text-slate-500">
            {liabilityCount}{" "}
            {liabilityCount === 1 ? "liability" : "liabilities"}
          </div>
        </div>
      </div>

      {/* Leverage bar */}
      <div className="mt-4">
        <div className="flex justify-between text-[11px] text-slate-500 mb-1">
          <span>Debt vs assets</span>
          <span className={debtRatio > 50 ? "text-rose-600 font-semibold" : ""}>
            {Number(debtRatio || 0).toFixed(0)}%
          </span>
        </div>
        <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
          <div
            className={`h-full rounded-full ${
              debtRatio > 50
                ? "bg-rose-500"
                : debtRatio > 25
                  ? "bg-amber-500"
                  : "bg-emerald-500"
            }`}
            style={{ width: `${Math.min(Number(debtRatio || 0), 100)}%` }}
          />
        </div>
      </div>

      {/* Trend sparkline */}
      {projection?.length > 1 && (
        <div className="mt-5 pt-4 border-t border-slate-100 dark:border-slate-800">
          <div className="flex items-end justify-between mb-2">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">
              Projected in 10 yrs
            </div>
            <div className="text-right">
              <span className="num-mono text-sm font-bold text-slate-900 dark:text-white">
                {fmtINR(projected, { compact: true })}
              </span>
              {growthPct != null && (
                <span className="ml-1.5 inline-flex items-center text-[11px] font-semibold text-emerald-600">
                  <ArrowUpRight className="h-3 w-3" />
                  {growthPct >= 0 ? "+" : ""}
                  {growthPct.toFixed(0)}%
                </span>
              )}
            </div>
          </div>
          <div className="h-16">
            <ResponsiveContainer>
              <AreaChart
                data={projection}
                margin={{ top: 2, bottom: 0, left: 0, right: 0 }}
              >
                <defs>
                  <linearGradient id="nwSpark" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Tooltip
                  formatter={(v) => fmtINR(v)}
                  labelFormatter={(l) => `Year ${l}`}
                  contentStyle={{ fontSize: 12, borderRadius: 8 }}
                />
                <Area
                  type="monotone"
                  dataKey="net_worth"
                  stroke="#10b981"
                  strokeWidth={2}
                  fill="url(#nwSpark)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

// =====================================================================
// HEALTH — ring gauge + contributing factors
// =====================================================================
function HealthGauge({ score, debtRatio, insights }) {
  const s = score?.score ?? 0;
  const C = 2 * Math.PI * 42;
  const offset = C * (1 - s / 100);

  const factors = [
    {
      label: "Debt vs assets",
      value: `${Number(debtRatio || 0).toFixed(0)}%`,
      good: debtRatio <= 40,
    },
    {
      label: "Liquid assets",
      value: `${insights?.liquid_assets_percentage ?? 0}%`,
      good: (insights?.liquid_assets_percentage ?? 0) >= 10,
    },
    {
      label: "Diversification",
      value: insights?.diversification_score?.label || "—",
      good: (insights?.diversification_score?.score ?? 0) >= 60,
    },
  ];

  return (
    <div data-testid="stat-health" className="card-soft p-6 flex flex-col">
      <div className="text-xs uppercase tracking-widest text-slate-500">
        Financial Health
      </div>

      <div className="flex items-center gap-5 mt-3">
        <div className="relative h-28 w-28 shrink-0">
          <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
            <circle
              cx="50"
              cy="50"
              r="42"
              fill="none"
              strokeWidth="9"
              className="stroke-slate-100 dark:stroke-slate-800"
            />
            <circle
              cx="50"
              cy="50"
              r="42"
              fill="none"
              strokeWidth="9"
              strokeLinecap="round"
              stroke={HEALTH_STROKE(s)}
              strokeDasharray={C}
              strokeDashoffset={offset}
              style={{ transition: "stroke-dashoffset 0.8s ease" }}
            />
          </svg>
          <div className="absolute inset-0 grid place-items-center">
            <div className="text-center">
              <div
                className={`num-mono text-2xl font-bold ${HEALTH_ACCENT(s)}`}
              >
                {s}
              </div>
              <div className="text-[10px] text-slate-400 -mt-0.5">/ 100</div>
            </div>
          </div>
        </div>

        <div>
          <div className={`text-lg font-bold ${HEALTH_ACCENT(s)}`}>
            {score?.label || "—"}
          </div>
          <p className="text-xs text-slate-500 mt-1 leading-relaxed">
            Based on leverage, liquidity and diversification.
          </p>
        </div>
      </div>

      <div className="mt-5 space-y-2">
        {factors.map((f) => (
          <div
            key={f.label}
            className="flex items-center justify-between text-xs border-t border-slate-100 dark:border-slate-800 pt-2"
          >
            <span className="text-slate-500">{f.label}</span>
            <span
              className={`font-semibold ${
                f.good
                  ? "text-emerald-600"
                  : "text-slate-700 dark:text-slate-200"
              }`}
            >
              {f.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// =====================================================================
// PROJECTION — assets vs liabilities + net worth, 10y
// =====================================================================
function ProjectionCard({ projection }) {
  return (
    <div data-tour="wealth-projection" className="lg:col-span-2 card-soft p-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">
            Wealth Projection
          </div>
          <h3 className="font-display text-xl font-bold text-slate-900 dark:text-white mt-1">
            Assets vs Liabilities
          </h3>
        </div>
        <span className="text-[10px] uppercase tracking-widest bg-slate-100 dark:bg-slate-800 text-slate-500 px-2 py-0.5 rounded-full font-semibold">
          10 Years
        </span>
      </div>

      <div className="h-72 mt-4">
        <ResponsiveContainer>
          <AreaChart data={projection}>
            <defs>
              <linearGradient id="assetGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="liabilityGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ef4444" stopOpacity={0.2} />
                <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="rgba(148,163,184,0.2)"
            />
            <XAxis
              dataKey="year"
              tick={{ fontSize: 11 }}
              stroke="rgba(100,116,139,0.5)"
            />
            <YAxis
              tickFormatter={(v) => fmtINR(v, { compact: true })}
              tick={{ fontSize: 11 }}
              stroke="rgba(100,116,139,0.5)"
              width={56}
            />
            <Tooltip
              formatter={(v, n) => [fmtINR(v), n]}
              labelFormatter={(l) => `Year ${l}`}
              contentStyle={{ fontSize: 12, borderRadius: 8 }}
            />
            <Area
              type="monotone"
              dataKey="assets"
              stroke="#10b981"
              fill="url(#assetGrad)"
              strokeWidth={2.5}
              name="Assets"
            />
            <Area
              type="monotone"
              dataKey="net_worth"
              stroke="#2563eb"
              fill="none"
              strokeWidth={2.5}
              name="Net Worth"
            />
            <Area
              type="monotone"
              dataKey="liabilities"
              stroke="#ef4444"
              fill="url(#liabilityGrad)"
              strokeWidth={2}
              name="Liabilities"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="flex items-center gap-4 mt-2 text-[11px] text-slate-500">
        <Legend color="#10b981" label="Assets" />
        <Legend color="#2563eb" label="Net Worth" />
        <Legend color="#ef4444" label="Liabilities" />
      </div>
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="h-2 w-2 rounded-full"
        style={{ backgroundColor: color }}
      />
      {label}
    </span>
  );
}

// =====================================================================
// ALLOCATION — donut + compact legend + single insight
// =====================================================================
function AllocationDonut({ data, insights, classesTracked }) {
  const total = data.reduce((s, a) => s + a.value, 0);

  // Top 5 categories + aggregated "Others" for a clean legend/donut.
  const top = data.slice(0, 5);
  const othersVal = data.slice(5).reduce((s, a) => s + a.value, 0);
  const chartData =
    othersVal > 0 ? [...top, { name: "Others", value: othersVal }] : top;

  return (
    <div data-tour="allocation-section" className="card-soft p-6">
      <div className="text-xs uppercase tracking-widest text-slate-500">
        Asset Allocation
      </div>
      <h3 className="font-display text-xl font-bold text-slate-900 dark:text-white mt-1">
        Where your money lives
      </h3>

      {data.length === 0 ? (
        <p className="mt-8 text-sm text-slate-500 text-center">
          No assets yet.{" "}
          <Link to="/dashboard/assets" className="text-emerald-600">
            Add one →
          </Link>
        </p>
      ) : (
        <>
          <div className="relative h-48 mt-3">
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={58}
                  outerRadius={80}
                  paddingAngle={2}
                  stroke="none"
                >
                  {chartData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v) => fmtINR(v)}
                  contentStyle={{ fontSize: 12, borderRadius: 8 }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 grid place-items-center pointer-events-none">
              <div className="text-center">
                <div className="text-[10px] uppercase tracking-wider text-slate-400">
                  Total
                </div>
                <div className="num-mono text-lg font-bold text-slate-900 dark:text-white">
                  {fmtINR(total, { compact: true })}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 space-y-1.5">
            {chartData.map((item, i) => {
              const pct = total > 0 ? (item.value / total) * 100 : 0;
              return (
                <div
                  key={item.name}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                    <span
                      className="h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: COLORS[i % COLORS.length] }}
                    />
                    {item.name}
                  </span>
                  <span className="text-slate-400">
                    {pct < 1 ? "<1" : pct.toFixed(0)}%
                  </span>
                </div>
              );
            })}
          </div>

          {/* Detail tiles */}
          <div className="grid grid-cols-2 gap-2 mt-5">
            <DetailTile
              label="Largest"
              value={
                insights?.largest_allocation?.category
                  ? CATEGORY_LABELS[insights.largest_allocation.category] ||
                    insights.largest_allocation.category
                  : "—"
              }
              sub={`${insights?.largest_allocation?.percentage ?? 0}%`}
              subAccent="text-emerald-600"
            />
            <DetailTile
              label="Liquid"
              value={`${insights?.liquid_assets_percentage ?? 0}%`}
              sub="Accessible"
            />
            <DetailTile
              label="Diversified"
              value={`${insights?.diversification_score?.score ?? 0}/100`}
              sub={insights?.diversification_score?.label}
              subAccent="text-emerald-600"
            />
            <DetailTile label="Classes" value={classesTracked} sub="Tracked" />
          </div>

          {insights?.concentration_warning && (
            <div className="mt-3 rounded-xl border border-amber-200 dark:border-amber-500/20 bg-amber-50 dark:bg-amber-500/10 p-3 text-xs leading-relaxed text-amber-700 dark:text-amber-300">
              {insights.concentration_warning}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function DetailTile({ label, value, sub, subAccent = "text-slate-500" }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div className="mt-1 text-base font-bold text-slate-900 dark:text-white capitalize truncate">
        {value}
      </div>
      {sub && <div className={`text-xs mt-0.5 ${subAccent}`}>{sub}</div>}
    </div>
  );
}

// =====================================================================
// DEBT — compact KPIs + debt-over-time, or a clean debt-free state
// =====================================================================
function StressTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-2.5 text-xs shadow-lg">
      <div className="font-semibold text-slate-900 dark:text-white">
        {d.year}
      </div>
      <div
        className={`font-semibold ${STRESS_TEXT[d.label] || "text-slate-500"}`}
      >
        {d.label} pressure · {d.stress_score}/100
      </div>
      <div className="text-slate-500 mt-1">
        {d.active_loans} active loan{d.active_loans === 1 ? "" : "s"}
      </div>
      <div className="text-slate-500">EMI: {fmtINR(d.yearly_emi)}/yr</div>
    </div>
  );
}

function DebtCard({
  totalLiabilities,
  monthlyEMI,
  debtFreeYear,
  stressTimeline,
}) {
  const timeline = stressTimeline || [];

  if (!totalLiabilities || totalLiabilities <= 0) {
    return (
      <div data-tour="liabilities-section" className="card-soft p-6">
        <CardHeader
          icon={ShieldCheck}
          tone="emerald"
          eyebrow="Debt & Stress"
          title="Debt Pressure Forecast"
        />
        <div className="mt-8 flex flex-col items-center justify-center text-center py-8">
          <div className="grid h-14 w-14 place-items-center rounded-2xl bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600">
            <CheckCircle2 className="h-7 w-7" />
          </div>
          <div className="mt-3 font-display text-lg font-bold text-slate-900 dark:text-white">
            You&apos;re debt-free
          </div>
          <p className="mt-1 text-sm text-slate-500 max-w-xs">
            No outstanding liabilities, so there&apos;s no debt pressure on your
            future cash flow.
          </p>
        </div>
      </div>
    );
  }

  // Peak pressure year (highest stress score) for a one-line callout.
  const peak = timeline.reduce(
    (best, r) => (r.stress_score > (best?.stress_score ?? -1) ? r : best),
    null,
  );

  return (
    <div data-tour="liabilities-section" className="card-soft p-6">
      <CardHeader
        icon={ShieldAlert}
        tone="rose"
        eyebrow="Debt & Stress"
        title="Debt Pressure Forecast"
      />

      <div className="grid grid-cols-3 gap-3 mt-5">
        <KPI
          label="Outstanding"
          value={fmtINR(totalLiabilities, { compact: true })}
          accent="text-rose-600"
        />
        <KPI
          label="EMI / month"
          value={fmtINR(monthlyEMI, { compact: true })}
        />
        <KPI
          label="Debt-free by"
          value={debtFreeYear || "—"}
          accent={debtFreeYear ? "text-emerald-600" : "text-slate-400"}
        />
      </div>

      <div className="text-[11px] uppercase tracking-wider text-slate-400 mt-6 mb-1">
        Yearly debt pressure (0–100)
      </div>
      <div className="h-44">
        <ResponsiveContainer>
          <BarChart
            data={timeline}
            margin={{ top: 4, right: 4, left: 0, bottom: 0 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="rgba(148,163,184,0.2)"
            />
            <XAxis
              dataKey="year"
              tick={{ fontSize: 11 }}
              stroke="rgba(100,116,139,0.5)"
            />
            <YAxis
              domain={[0, 100]}
              tick={{ fontSize: 11 }}
              stroke="rgba(100,116,139,0.5)"
              width={32}
            />
            <Tooltip
              content={<StressTooltip />}
              cursor={{ fill: "rgba(148,163,184,0.12)" }}
            />
            <Bar dataKey="stress_score" radius={[6, 6, 0, 0]}>
              {timeline.map((d, i) => (
                <Cell key={i} fill={STRESS_COLOR[d.label] || "#94a3b8"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {peak && peak.stress_score > 0 && (
        <p className="mt-3 text-xs text-slate-500">
          Peak pressure around{" "}
          <span className="font-semibold text-slate-700 dark:text-slate-200">
            {peak.year}
          </span>{" "}
          (
          <span className={`font-semibold ${STRESS_TEXT[peak.label]}`}>
            {peak.label}
          </span>
          ) — {fmtINR(peak.yearly_emi)} of EMIs across {peak.active_loans} loan
          {peak.active_loans === 1 ? "" : "s"}.
        </p>
      )}
    </div>
  );
}

// =====================================================================
// MILESTONES — forward-looking timeline
// =====================================================================
function MilestonesCard({ milestones }) {
  const currentYear = new Date().getFullYear();
  const list = milestones || [];

  return (
    <div data-tour="goals-section" className="card-soft p-6">
      <CardHeader
        icon={Milestone}
        tone="emerald"
        eyebrow="Milestones"
        title="What's Ahead"
      />

      {list.length === 0 ? (
        <div className="mt-8 flex flex-col items-center justify-center text-center py-8">
          <div className="grid h-14 w-14 place-items-center rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-400">
            <PiggyBank className="h-7 w-7" />
          </div>
          <p className="mt-3 text-sm text-slate-500 max-w-xs">
            Add assets and goals to project the wealth milestones you&apos;ll
            hit over the next decade.
          </p>
        </div>
      ) : (
        <div className="mt-6 relative pl-6">
          {/* vertical line */}
          <div className="absolute left-[7px] top-1 bottom-1 w-px bg-slate-200 dark:bg-slate-700" />
          <div className="space-y-5">
            {list.map((m, i) => {
              const isDebtFree = m.type === "debt_free";
              const hasYear = m.year != null;
              const yearsAway = hasYear
                ? Math.max(0, m.year - currentYear)
                : null;
              const estimate = !m.within_projection;

              const away = !hasYear
                ? "long-term"
                : yearsAway === 0
                  ? "this year"
                  : `${estimate ? "~" : ""}${yearsAway} yr${yearsAway > 1 ? "s" : ""}`;

              const sub = !hasYear
                ? "Beyond your current trend"
                : estimate
                  ? `Projected ≈ ${m.year}`
                  : `Expected by ${m.year}`;

              return (
                <div key={i} className="relative">
                  <span
                    className={`absolute -left-6 top-0.5 h-3.5 w-3.5 rounded-full ring-4 ring-white dark:ring-slate-900 ${
                      isDebtFree
                        ? "bg-blue-500"
                        : hasYear
                          ? "bg-emerald-500"
                          : "bg-slate-300 dark:bg-slate-600"
                    }`}
                  />
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-semibold text-slate-900 dark:text-white text-sm">
                      {m.label}
                    </div>
                    <div className="text-xs text-slate-400 whitespace-nowrap">
                      {away}
                    </div>
                  </div>
                  <div className="text-xs text-slate-500">{sub}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// =====================================================================
// Small shared pieces
// =====================================================================
function CardHeader({ icon: Icon, tone, eyebrow, title }) {
  const toneCls =
    tone === "rose"
      ? "bg-rose-50 dark:bg-rose-500/10 text-rose-600"
      : "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600";
  return (
    <div className="flex items-start gap-3">
      <div
        className={`grid h-10 w-10 place-items-center rounded-xl ${toneCls}`}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500">
          {eyebrow}
        </div>
        <h3 className="font-display text-xl font-bold text-slate-900 dark:text-white mt-1">
          {title}
        </h3>
      </div>
    </div>
  );
}

function KPI({ label, value, accent = "text-slate-900 dark:text-white" }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div className={`num-mono text-base font-bold mt-1 ${accent}`}>
        {value}
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 h-64 rounded-2xl bg-slate-100 dark:bg-slate-800" />
        <div className="h-64 rounded-2xl bg-slate-100 dark:bg-slate-800" />
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 h-80 rounded-2xl bg-slate-100 dark:bg-slate-800" />
        <div className="h-80 rounded-2xl bg-slate-100 dark:bg-slate-800" />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="h-72 rounded-2xl bg-slate-100 dark:bg-slate-800" />
        <div className="h-72 rounded-2xl bg-slate-100 dark:bg-slate-800" />
      </div>
    </div>
  );
}

// =====================================================================
// PROFILE COMPLETION GATE
// Web requires phone + DOB. If a logged-in user is missing either, block
// the dashboard with an explanation until they provide them.
// =====================================================================
function ProfileCompletionGate() {
  const { user, refresh } = useAuth();
  const needsPhone = !user?.phone;
  const needsDob = !user?.date_of_birth;

  const [phone, setPhone] = useState("");
  const [dob, setDob] = useState("");
  const [saving, setSaving] = useState(false);

  if (!user) return null;
  // Demo accounts are zero-friction — never prompt them for phone/DOB.
  if (user.is_demo) return null;
  if (!needsPhone && !needsDob) return null;

  const save = async () => {
    if (needsPhone && phone.length < 10) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }
    if (needsDob && !dob) {
      toast.error("Please enter your date of birth");
      return;
    }
    setSaving(true);
    try {
      const payload = {};
      if (needsPhone) payload.phone = phone.trim();
      if (needsDob) payload.date_of_birth = dob;
      await api.patch("/auth/profile", payload);
      await refresh();
      toast.success("Profile completed");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md card-soft p-6">
        <div className="text-xs uppercase tracking-widest text-slate-500">
          One quick step
        </div>
        <h2 className="font-display text-2xl font-bold text-slate-900 dark:text-white mt-1">
          Complete your profile
        </h2>
        <p className="mt-2 text-sm text-slate-500">
          We need a couple of details to tailor AccessWealth to you.
        </p>

        <div className="mt-5 space-y-4">
          {needsDob && (
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                Date of birth
              </label>
              <DateField
                data-testid="gate-dob"
                value={dob}
                max={new Date().toISOString().slice(0, 10)}
                toYear={new Date().getFullYear()}
                onChange={(v) => setDob(v)}
              />
              <p className="text-[11px] text-slate-500">
                Used to calculate your retirement age and the corpus you&apos;ll
                need.
              </p>
            </div>
          )}

          {needsPhone && (
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
                Phone number
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="tel"
                  maxLength={10}
                  data-testid="gate-phone"
                  value={phone}
                  onChange={(e) =>
                    setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))
                  }
                  className="input-soft !pl-10"
                  placeholder="9876543210"
                />
              </div>
              <p className="text-[11px] text-slate-500">
                Lets family members find and link to you for shared planning.
              </p>
            </div>
          )}
        </div>

        <button
          onClick={save}
          disabled={saving}
          data-testid="gate-save"
          className="btn-primary w-full mt-6"
        >
          {saving ? "Saving…" : "Save & continue"}
        </button>
      </div>
    </div>
  );
}
